"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowRight, Calendar, FileText, TrendingUp } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function InsightsGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} className="flex flex-col space-y-4" data-index={i}>
            <div className="h-[240px] w-full animate-pulse rounded-xl bg-muted" />
            <div className="space-y-2">
              <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
        <p className="text-muted-foreground mb-4">Unable to load insights at this time.</p>
        <Button onClick={refetch} variant="outline">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
        <p className="text-muted-foreground">No articles found.</p>
      </div>;
  }
  return <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || "";
      const isTechnical = categoryName.toLowerCase().includes("technical") || categoryName.toLowerCase().includes("white paper");
      const BadgeIcon = isTechnical ? FileText : TrendingUp;
      const badgeLabel = isTechnical ? "Technical Paper" : "Market Trend";
      const plainExcerpt = post.excerpt?.replace(/<[^>]+>/g, "") || "";
      return <Card key={post.id} className="group overflow-hidden border-border bg-card transition-all hover:shadow-lg" data-index={i}>
            <div className="relative">
              <AspectRatio ratio={16 / 9}>
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1567261/img/industrial_placeholder_generic.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </AspectRatio>
              <div className="absolute top-4 right-4">
                <Badge variant="secondary" className="bg-background/90 text-foreground shadow-sm backdrop-blur-sm">
                  <BadgeIcon className="mr-1 h-3 w-3" />
                  {badgeLabel}
                </Badge>
              </div>
            </div>

            <CardHeader className="pb-3">
              <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
                <span>
                  {post.date ? new Date(post.date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric"
              }) : "Recent"}
                </span>
              </div>
              <CardTitle className="font-sans text-xl font-bold leading-tight text-foreground group-hover:text-ring">
                <Link to={post.link || "#"}>
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>

            <CardContent className="pb-6">
              <p className="text-muted-foreground line-clamp-2 text-sm leading-relaxed">
                {plainExcerpt}
              </p>
            </CardContent>

            <CardFooter className="pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent hover:text-ring">
                  Read Article
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function IndustrialInsights() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}